import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building2, CalendarClock, ChevronRight, Loader2, Play, Search, UserRound, X } from "lucide-react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PayrollTemplateDrawer } from "./PayrollTemplateDrawer";

const withLedger = (path, ledger) => `${path}${path.includes("?") ? "&" : "?"}ledger=${encodeURIComponent(ledger)}`;

export const PayrollTemplatesSection = ({ ledger, colonies, onChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState("");
  const [search, setSearch] = useState("");
  const [touchRows, setTouchRows] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(withLedger("/payroll/templates", ledger));
      setTemplates(response.data || []);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  }, [ledger]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse), (max-width: 1024px)");
    const update = () => setTouchRows(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  const visibleTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((template) => (
      (template.worker_name || "").toLowerCase().includes(term) ||
      (template.colony_name || "").toLowerCase().includes(term)
    ));
  }, [templates, search]);

  const save = async (workerId, form) => {
    const response = await api.put(`/payroll/templates/${workerId}`, { ...form, ledger });
    setTemplates((rows) => rows.map((row) => row.worker_user_id === workerId ? response.data : row));
    setEditing(response.data);
    onChanged?.();
    return response.data;
  };

  const runNow = async (template, action) => {
    setRunning(`${template.worker_user_id}-${action}`);
    try {
      const response = await api.post(`/payroll/templates/${template.worker_user_id}/run-now/${action}`);
      const count = action === "payroll" ? (response.data?.created ? 1 : 0) : Number(response.data?.created || 0);
      if (action === "payroll") toast.success(count ? "Payroll record created" : "That payroll period already exists");
      else {
        const result = response.data?.invoices?.[0];
        if (!count) toast.info("No unbilled payroll was waiting.");
        else if (result?.email_sent) toast.success(`${count} charge${count === 1 ? "" : "s"} invoiced and emailed to ${result.email_to}.`);
        else if (result?.email_error) toast.warning(`${count} charge${count === 1 ? "" : "s"} saved on a draft invoice. ${result.email_error}`);
        else toast.success(`${count} charge${count === 1 ? "" : "s"} saved on a draft payroll invoice.`);
      }
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setRunning("");
    }
  };

  return (
    <div className="management-section space-y-3" data-testid="payroll-templates-section">
      <div>
        <h1 className="management-title font-display font-bold" data-testid="payroll-templates-heading">Worker payroll templates</h1>
        <p className="text-xs text-muted-foreground" data-testid="payroll-templates-description">{touchRows ? "Double-tap a worker row to edit payroll and billing setup." : "Click anywhere on a worker row to edit payroll and billing setup."}</p>
      </div>
      <div className="relative max-w-md" data-testid="payroll-template-search-wrap">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search workers" className="pl-9 pr-10" aria-label="Search payroll workers" data-testid="payroll-template-search" />
        {search && <button type="button" onClick={() => setSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground" aria-label="Clear worker search" data-testid="payroll-template-search-clear"><X className="w-4 h-4" /></button>}
      </div>
      <div className="management-list border-y border-border divide-y divide-border" data-testid="payroll-templates-list">
        {loading ? (
          <div className="py-8 flex items-center justify-center text-sm text-muted-foreground" data-testid="payroll-templates-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading workers…</div>
        ) : templates.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="payroll-templates-empty">Add workers from Manage workers before creating templates.</p>
        ) : visibleTemplates.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="payroll-template-search-empty">No workers match “{search}”.</p>
        ) : visibleTemplates.map((template) => (
          <TemplateSummaryRow key={template.worker_user_id} template={template} running={running} touchMode={touchRows} onOpen={setEditing} onRun={runNow} />
        ))}
      </div>
      <PayrollTemplateDrawer template={editing} colonies={colonies} onOpenChange={(open) => { if (!open) setEditing(null); }} onSave={save} />
    </div>
  );
};

const TemplateSummaryRow = ({ template, running, touchMode, onOpen, onRun }) => {
  const id = template.worker_user_id;
  const [armed, setArmed] = useState(false);
  const lastTapRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const activate = (event) => {
    if (!touchMode || event.detail === 0) {
      onOpen(template);
      return;
    }
    const now = Date.now();
    if (now - lastTapRef.current < 650) {
      window.clearTimeout(timerRef.current);
      setArmed(false);
      lastTapRef.current = 0;
      onOpen(template);
      return;
    }
    lastTapRef.current = now;
    setArmed(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setArmed(false), 700);
  };

  return (
    <div className={`management-list-row group relative grid grid-cols-2 sm:grid-cols-12 gap-3 px-3 py-3.5 items-center transition-[background-color,border-color,box-shadow] duration-200 ${armed ? "bg-primary/10 shadow-[inset_3px_0_0_hsl(var(--primary))]" : "hover:bg-secondary/45"}`} data-testid={`payroll-template-row-${id}`}>
      <button type="button" onClick={activate} className="absolute inset-0 z-0 touch-manipulation rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" aria-label={touchMode ? `Double-tap to edit ${template.worker_name}` : `Edit ${template.worker_name}`} title={touchMode ? "Double-tap to edit" : "Click to edit"} data-testid={`payroll-template-open-${id}`} />
      <div className="relative z-10 pointer-events-none col-span-2 sm:col-span-3 flex items-center gap-3" data-testid={`payroll-template-worker-${id}`}>
        <div className="management-template-avatar h-9 w-9 shrink-0 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><UserRound className="w-4 h-4" /></div>
        <div className="min-w-0"><div className="font-semibold truncate">{template.worker_name}</div>{touchMode && <div className={`text-[11px] ${armed ? "text-primary font-semibold" : "text-muted-foreground"}`}>{armed ? "Tap again to edit" : "Double-tap to edit"}</div>}</div>
      </div>
      <div className="relative z-10 pointer-events-none sm:col-span-2" data-testid={`payroll-template-amount-${id}`}><div className="text-[10px] uppercase text-muted-foreground sm:hidden">Worker payroll</div><div className={`text-sm mono-num font-semibold ${template.gross_pay ? "text-foreground" : "text-amber-700"}`}>{template.gross_pay ? fmtMoney(template.gross_pay) : "Not configured"}</div></div>
      <div className="relative z-10 pointer-events-none sm:col-span-2 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`payroll-template-colony-summary-${id}`}><Building2 className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{template.colony_name || "No colony"}</span></div>
      <AutomationStatus label="Payroll" enabled={template.auto_payroll_enabled} frequency={template.payroll_frequency} next={template.payroll_next_run_on} testId={`payroll-template-payroll-status-${id}`} />
      <AutomationStatus label="Billing" enabled={template.auto_billing_enabled} frequency={template.billing_frequency} next={template.billing_next_run_on} testId={`payroll-template-billing-status-${id}`} />
      <div className="relative z-20 col-span-2 sm:col-span-1 flex justify-end items-center gap-1">
        {template.auto_payroll_enabled && <Button size="icon" variant="ghost" onClick={() => onRun(template, "payroll")} disabled={!!running} aria-label={`Create payroll now for ${template.worker_name}`} data-testid={`payroll-template-run-payroll-${id}`}>{running === `${id}-payroll` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}</Button>}
        {template.auto_billing_enabled && <Button size="icon" variant="ghost" onClick={() => onRun(template, "billing")} disabled={!!running} aria-label={`Create billing now for ${template.worker_name}`} data-testid={`payroll-template-run-billing-${id}`}>{running === `${id}-billing` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}</Button>}
        <ChevronRight className="w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
};

const AutomationStatus = ({ label, enabled, frequency, next, testId }) => (
  <div className="relative z-10 pointer-events-none sm:col-span-2 text-xs" data-testid={testId}>
    <div className={`font-semibold flex items-center gap-1.5 ${enabled ? "text-emerald-700" : "text-muted-foreground"}`}><span className={`h-1.5 w-1.5 rounded-full ${enabled ? "bg-emerald-600" : "bg-muted-foreground/40"}`} />{label}: {enabled ? frequency : "Off"}</div>
    {enabled && <div className="text-muted-foreground">Next {next || "after save"}</div>}
  </div>
);